import { Divider } from 'antd';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import GlobalFooter from 'components/GlobalFooter';
import { Collapse, WarningAlert } from 'components/ui';
import { ErrorBoundary as BugSnagErrorBoundary } from 'utils/bugsnag';
import { Theme } from 'utils/color/horizonTheme';
import { FULL_TURBINE_IMAGE, FULL_TURBINE_IMAGE_WHITE } from 'utils/constants';
import { useFeatures } from 'utils/features';

import {
  ErrorContainer,
  FallbackContainer,
  StyledButton,
  StyledCollapse,
  StyledError,
  StyledErrorMessage,
  StyledImage,
  StyledText,
  StyledTitle,
} from './ErrorBoundary.style';

const { Panel } = Collapse;

interface IErrorBoundaryContentProps {
  // Main error that prompted the boundary. The stacktrace from the error may be displayed
  error: Error;
  // Not currently used, but a required field for the BugSnagErrorBoundary
  info: React.ErrorInfo;
  // Callback provided by BugsnagErrorBoundary that attempts to re-render child components
  clearError: () => void;
}

const ErrorBoundaryContent: React.FunctionComponent<IErrorBoundaryContentProps> = ({
  error,
  info: _info,
  clearError,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const BaseError = memo(() => (
    <StyledError>
      <StyledImage
        alt=""
        src={theme.themeName === Theme.Dark ? FULL_TURBINE_IMAGE_WHITE : FULL_TURBINE_IMAGE}
        // data-private used to hide images from LogRocket: https://docs.logrocket.com/reference/dom
      />
      <StyledTitle level={3}>
        {t('errorBoundary.title', 'Sorry, we are having problems with our system.')}
      </StyledTitle>
      <StyledText>
        {t(
          'errorBoundary.message',
          'We have been notified of the error you are experiencing and will review it promptly. Please refresh the page or contact support.'
        )}
      </StyledText>
    </StyledError>
  ));

  try {
    // Jo 4/19/23 - On the off chance that the useFeatures hook itself throws an error,
    // we need to be able to catch it and still display a basic error message.
    // Linter gets upset because you really shouldn't be calling features conditionally within a try/catch like this.
    // eslint-disable-next-line
    const { INTERNAL_TESTER } = useFeatures().features;
    return (
      <ErrorContainer>
        <BaseError />
        {INTERNAL_TESTER && error && (
          <>
            <StyledButton type="primary" onClick={clearError}>
              {t('errorBoundary.reload', 'Reload')}
            </StyledButton>
            <Divider />
            <StyledCollapse ghost>
              <Panel header={t('errorBoundary.moreInformation', 'More information')} key="1">
                <WarningAlert message={<StyledErrorMessage>{error?.stack}</StyledErrorMessage>} />
              </Panel>
            </StyledCollapse>
          </>
        )}
      </ErrorContainer>
    );
  } catch (e) {
    return (
      <FallbackContainer>
        <ErrorContainer>
          <BaseError />
        </ErrorContainer>
        <GlobalFooter />
      </FallbackContainer>
    );
  }
};

interface IProps {
  children: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
}

export const ErrorBoundary: React.FunctionComponent<IProps> = ({ children }) => {
  return <BugSnagErrorBoundary FallbackComponent={ErrorBoundaryContent} children={children} />;
};
